"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { BorderBeam } from "border-beam";
import { useTheme } from "next-themes";

import { cn } from "@/lib/utils";
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalTitle,
} from "@/components/ui/modal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Icon } from "@/components/ui/icon";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-xl bg-surface-elevated text-heading",
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  className,
  showCloseButton = false,
  shouldFilter,
  ...props
}: React.ComponentProps<typeof Modal> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  /** Pass false when results are filtered server-side (async search). */
  shouldFilter?: boolean;
}) {
  return (
    <Modal {...props}>
      {/* Desktop (lg+): the modal shell goes fully transparent so the search
          pill and results panel read as two independent floating surfaces.
          Below lg the standard drawer/modal chrome is kept. */}
      <ModalContent
        size="md"
        className={cn(
          "t-modal overflow-hidden p-0 lg:top-[22%] lg:translate-y-0 lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none",
          className,
        )}
      >
        <div className="sr-only">
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
        </div>
        {showCloseButton && (
          <ModalClose asChild>
            <button
              type="button"
              className="absolute top-3 right-3 z-10 flex items-center justify-center size-7 rounded-md hover:bg-contrast/6 transition-colors cursor-pointer text-contrast/40 hover:text-contrast/70"
            >
              <Icon name="IconCrossMedium" size={14} />
            </button>
          </ModalClose>
        )}
        <Command
          shouldFilter={shouldFilter}
          className="rounded-none bg-transparent lg:gap-3 lg:overflow-visible [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-contrast/45"
        >
          {children}
        </Command>
      </ModalContent>
    </Modal>
  );
}

/**
 * Standalone search pill for the command palette — an independent rounded
 * surface wrapped in the `border-beam` traveling gold beam.
 * Pass `value`/`onValueChange` to get the inline clear button.
 */
function CommandSearchInput({
  className,
  value,
  onValueChange,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The palette mounts inside `Modal`, whose popup sets `initialFocus={false}`
  // — focus the input ourselves once the open animation has started.
  React.useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <BorderBeam
      data-slot="command-search-pill"
      size="md"
      colorVariant="sunset"
      // Light preset opacities are much lower, so run it at full strength there.
      strength={isLight ? 1 : 0.7}
      // Rotate the sunset palette's red-orange stops toward the brand gold
      // and keep the hue swing tight so the beam never drifts back to red.
      hueRange={8}
      borderRadius={24}
      theme={isLight ? "light" : "dark"}
      className="m-3 shrink-0 lg:m-0"
      style={{ "--beam-hue-base": "16deg" } as React.CSSProperties}
    >
      <div className="flex h-12 items-center gap-3 rounded-full border border-contrast/10 bg-surface-elevated pl-4.5 pr-3">
        <Icon
          name="IconMagnifyingGlass"
          size={18}
          className="shrink-0 text-heading"
        />
        <CommandPrimitive.Input
          ref={inputRef}
          data-slot="command-input"
          autoFocus
          value={value}
          onValueChange={onValueChange}
          className={cn(
            "h-full w-full min-w-0 flex-1 bg-transparent text-[15px] text-heading outline-hidden placeholder:text-contrast/35 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />
        {value ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onValueChange?.("")}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-contrast/35 transition-colors hover:bg-contrast/6 hover:text-contrast/70"
          >
            <Icon name="IconCrossMedium" size={12} />
          </button>
        ) : null}
        <KbdShortcut keys={["ESC"]} size="md" className="shrink-0" />
      </div>
    </BorderBeam>
  );
}

/**
 * Detached results surface rendered below the search pill. Wrap
 * `CommandList` (and an optional footer) in this when using the floating
 * palette layout.
 */
function CommandPanel({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="command-panel"
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-elevated lg:rounded-2xl lg:shadow-[var(--modal-shadow)]",
        className,
      )}
      {...props}
    />
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="flex h-12 items-center gap-2.5 border-b border-contrast/6 px-4"
    >
      <Icon
        name="IconMagnifyingGlass"
        size={18}
        className="shrink-0 text-contrast/30"
      />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "flex h-full w-full bg-transparent text-sm text-heading outline-hidden placeholder:text-contrast/30 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn("flex flex-col overflow-x-hidden", className)}
      {...props}
    >
      {/* y-only: long result titles must truncate, never sideways-scroll.
          `constrainContentWidth` is required — Base UI's ScrollArea content
          defaults to `min-width: fit-content`, which defeats truncation and
          lets long names push the panel into horizontal scroll. */}
      <ScrollArea
        constrainContentWidth
        className="min-h-0 overflow-y-auto overflow-x-hidden h-full max-h-[340px]"
      >
        {children}
      </ScrollArea>
    </CommandPrimitive.List>
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-8 text-center text-sm text-contrast/40"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn("overflow-hidden px-3 py-2 text-heading", className)}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("h-px bg-contrast/6", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-contrast/70 outline-hidden select-none transition-colors data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-contrast/6 data-[selected=true]:text-heading",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "ml-auto text-xs tracking-widest text-contrast/30",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandSearchInput,
  CommandPanel,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
